import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatPrice } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";

export default function Shop() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const TYPES = [
    { v: "", label: t("shop.allTypes") },
    { v: "extra_virgin", label: t("shop.typeExtraVirgin") },
    { v: "virgin", label: t("shop.typeVirgin") },
    { v: "flavored", label: t("shop.typeFlavored") },
    { v: "blended", label: t("shop.typeBlended") },
  ];
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regionCounts, setRegionCounts] = useState({});
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [type, setType] = useState(searchParams.get("type") || "");
  const [region, setRegion] = useState(searchParams.get("region") || "");
  const [organic, setOrganic] = useState(searchParams.get("organic") === "true");
  const [goldPressed, setGoldPressed] = useState(searchParams.get("gold_pressed") === "true");
  const [price, setPrice] = useState([0, 100]);
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    api.get("/products/region-counts").then((r) => setRegionCounts(r.data)).catch(() => {});
  }, []);

  // Region quick-pick chips. Values are case-insensitive substrings matched against the product's `region` field by the backend.
  const REGION_CHIPS = [
    { v: "", labelKey: "shop.regionAll" },
    { v: "Istria", labelKey: "shop.regionIstria" },
    { v: "Hvar", labelKey: "shop.regionHvar" },
    { v: "Brač", labelKey: "shop.regionBrac" },
    { v: "Korčula", labelKey: "shop.regionKorcula" },
  ];

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      const params = {};
      if (q) params.q = q;
      if (type) params.type = type;
      if (region) params.region = region;
      if (organic) params.organic = true;
      if (goldPressed) params.gold_pressed = true;
      if (price[0] > 0) params.min_price = price[0];
      if (price[1] < 100) params.max_price = price[1];
      if (sort) params.sort = sort;
      try {
        const { data } = await api.get("/products", { params });
        setProducts(data);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [q, type, region, organic, goldPressed, price, sort]);

  useEffect(() => {
    const sp = new URLSearchParams();
    if (q) sp.set("q", q);
    if (type) sp.set("type", type);
    if (region) sp.set("region", region);
    if (organic) sp.set("organic", "true");
    if (goldPressed) sp.set("gold_pressed", "true");
    setSearchParams(sp, { replace: true });
  }, [q, type, region, organic, goldPressed, setSearchParams]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="shop-page">
      <p className="overline">{t("shop.overline")}</p>
      <h1 className="font-serif text-5xl lg:text-6xl tracking-tight mt-2">{t("shop.title")}</h1>
      <p className="text-ink-soft mt-4 max-w-2xl">
        {loading ? t("shop.loadingMsg") : t("counts.results", { count: products.length, vendors: new Set(products.map(p => p.vendor_name)).size })}
      </p>

      {/* Region quick-pick chips — let buyers browse Croatian olive oils by region in one click */}
      <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label={t("shop.filterRegion")} data-testid="region-chips">
        {REGION_CHIPS.map((chip) => {
          const active = region === chip.v;
          const count = chip.v ? regionCounts[chip.v]?.vendors : Object.values(regionCounts).reduce((s, r) => s + (r.vendors || 0), 0);
          return (
            <button
              key={chip.v || "all"}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setRegion(chip.v)}
              data-testid={`region-chip-${chip.v || "all"}`}
              className={
                "px-4 py-2 rounded-full text-sm tracking-wide transition-all border inline-flex items-center gap-2 " +
                (active
                  ? "bg-olive text-bone border-olive shadow-[0_4px_18px_rgba(82,93,55,0.18)]"
                  : "bg-white text-ink-soft border-edge hover:border-olive hover:text-olive")
              }
            >
              <span>{t(chip.labelKey)}</span>
              {count > 0 && (
                <span
                  className={
                    "text-[10px] font-mono px-1.5 py-0.5 rounded-full leading-none " +
                    (active ? "bg-bone/20 text-bone" : "bg-boneAlt text-ink-muted")
                  }
                  data-testid={`region-chip-count-${chip.v || "all"}`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-12 mt-12">
        <aside className="space-y-8" data-testid="shop-filters">
          <div>
            <label className="overline">{t("shop.filterSearch")}</label>
            <input
              type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={t("shop.filterSearchPlaceholder")}
              className="mt-2 w-full bg-white hairline px-3 py-2 rounded-sm text-sm"
              data-testid="filter-search"
            />
          </div>
          <div>
            <label className="overline">{t("shop.filterType")}</label>
            <Select value={type || "all"} onValueChange={(v) => setType(v === "all" ? "" : v)}>
              <SelectTrigger className="mt-2 rounded-sm border-edge bg-white" data-testid="filter-type">
                <SelectValue placeholder={t("shop.allTypes")} />
              </SelectTrigger>
              <SelectContent>
                {TYPES.map((it) => (
                  <SelectItem key={it.v || "all"} value={it.v || "all"}>{it.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="overline">{t("shop.filterRegion")}</label>
            <input
              type="text" value={region} onChange={(e) => setRegion(e.target.value)}
              placeholder={t("shop.filterRegionPlaceholder")}
              className="mt-2 w-full bg-white hairline px-3 py-2 rounded-sm text-sm"
              data-testid="filter-region"
            />
          </div>
          <div>
            <label className="overline">{t("shop.filterPrice")} · {formatPrice(price[0], lang)} – {formatPrice(price[1], lang)}</label>
            <Slider
              min={0} max={100} step={2}
              value={price} onValueChange={setPrice}
              className="mt-4"
              data-testid="filter-price"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer" data-testid="filter-organic-label">
            <Checkbox checked={organic} onCheckedChange={setOrganic} data-testid="filter-organic" />
            <span className="text-sm">{t("shop.filterOrganic")}</span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer" data-testid="filter-gold-pressed-label">
            <Checkbox checked={goldPressed} onCheckedChange={setGoldPressed} data-testid="filter-gold-pressed" className="mt-0.5" />
            <span className="text-sm">
              {t("shop.filterGoldPressed")}
              <span className="block text-[11px] text-ink-muted mt-0.5">{t("shop.filterGoldPressedHint")}</span>
            </span>
          </label>
          <div>
            <label className="overline">{t("shop.filterSort")}</label>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="mt-2 rounded-sm border-edge bg-white" data-testid="filter-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">{t("shop.sortNewest")}</SelectItem>
                <SelectItem value="price_asc">{t("shop.sortPriceAsc")}</SelectItem>
                <SelectItem value="price_desc">{t("shop.sortPriceDesc")}</SelectItem>
                <SelectItem value="rating">{t("shop.sortRating")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </aside>

        <div data-testid="shop-results">
          {loading ? (
            <p className="text-ink-muted">{t("common.loading")}</p>
          ) : products.length === 0 ? (
            <div className="text-center py-24 bg-boneAlt rounded-sm">
              <p className="font-serif text-3xl">{t("shop.noResults")}</p>
              <p className="text-ink-soft mt-2">{t("shop.tryWider")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8">
              {products.map((p) => <ProductCard key={p.product_id} product={p} testid="shop-product" />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
